/**
 * Why focus moved (W1-12 ui-kit-i18n): tooltips open on focus only when the user moved focus with the keyboard.
 *
 * Focus also moves for other reasons: a dialog, sheet or popover focuses its first control when it opens, a
 * closing layer gives focus back to its trigger, a pointer press focuses what it hits, a feature calls focus().
 * A tooltip opened by one of those would sit on top of the overlay stack, and the next Escape (meant to close the
 * dialog, or for the editor) would only dismiss that tooltip. `:focus-visible` cannot tell these apart: after
 * Enter or Escape the browser treats the programmatic focus as keyboard focus as well.
 *
 * So the kit records the last input event (window capture phase, before any handler can stop it):
 *  - a navigation key (Tab, Shift+Tab, arrows, Home, End, PageUp, PageDown, F6) without Ctrl / Alt / Meta;
 *  - anything else (other keys, pointer presses).
 * Focus counts as keyboard navigation when the last input was a navigation key pressed less than
 * NAV_FOCUS_WINDOW_MS ago. The window covers roving-focus widgets that move focus in a timeout after the key.
 */

const NAV_KEYS = new Set([
  'Tab',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
  'PageUp',
  'PageDown',
  'F6',
]);

/** How long after a navigation key a focus change still counts as caused by it. */
export const NAV_FOCUS_WINDOW_MS = 1000;

interface LastInput {
  readonly navigation: boolean;
  readonly at: number;
}

let last: LastInput | null = null;
let installed = false;

const now = (): number => (typeof performance === 'undefined' ? Date.now() : performance.now());

function onKeyDown(e: KeyboardEvent): void {
  last = { navigation: NAV_KEYS.has(e.key) && !e.ctrlKey && !e.altKey && !e.metaKey, at: now() };
}

function onPointer(): void {
  last = { navigation: false, at: now() };
}

/** Starts recording input events (idempotent; this module calls it when it loads). */
export function installFocusIntent(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('keydown', onKeyDown, true);
  window.addEventListener('pointerdown', onPointer, true);
  window.addEventListener('mousedown', onPointer, true);
  window.addEventListener('touchstart', onPointer, { capture: true, passive: true });
}

/** True when the focus change happening now was caused by a keyboard navigation key (see module comment). */
export function focusFromKeyboardNavigation(): boolean {
  return last !== null && last.navigation && now() - last.at < NAV_FOCUS_WINDOW_MS;
}

installFocusIntent();
